import { useCallback, useEffect, useState } from "react";

import { useAppState } from "@/components/AppStateProvider";
import Box from "@/components/Box";
import FeaturedTestProductCard from "@/components/organisations/shop/FeaturedTestProductCard";
import { useLocalesContext } from "@/contexts/Locale";
import { useTestProductModalContext } from "@/contexts/TestProductModalContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { COMING_SOON, OUT_OF_STOCK } from "@/core/constants";
import { PRACTITIONER_TEST_PRODUCT_CONNECTIONS_QUERY } from "@/graphql/shop";
import useWindowSize from "@/hooks/use-window-size";
import Center from "@/tpo/Center";
import { GilroyFontCurrency } from "@/tpo/Currency";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";
import { useQuery } from "@apollo/client";

import { CMP_PERMISSION_PURCHASE_PRODUCTS } from "../constants";
import { SHOP_BREAKPOINTS } from "./shopTheme";

const shopMobileMaxWidth = Number.parseInt(SHOP_BREAKPOINTS.sm, 10);

function FeaturedTestProductSlide({ testProduct }) {
  const { organisation } = useOrganisationContext();
  const { basket, hasBasketGotProduct, addProductToBasket, removeProductFromBasket } =
    useOrganisationBasketContext();
  const { selectTestProductId } = useTestProductModalContext();
  const { setPractitionerBasketOpen: setBasketOpen } = useAppState();
  const { width } = useWindowSize();

  const permissions = organisation?.permissions || [];
  const useMobileLayout = !width || width < shopMobileMaxWidth;

  const handleAddToBasket = useCallback(
    product => {
      if (!basket) return;
      if (basket.multiplePatientsEnabled && product.isCyrex && !hasBasketGotProduct(product.id)) {
        return;
      }
      const allowMoreThanOne = basket?.purchaseAsStock || basket?.multiplePatientsEnabled;
      if (product.options?.length || (!allowMoreThanOne && product.addons?.length)) {
        selectTestProductId(product.id);
        return;
      }
      addProductToBasket(product.id);
      selectTestProductId(null);
      setBasketOpen(true);
    },
    [addProductToBasket, selectTestProductId, setBasketOpen, basket, hasBasketGotProduct]
  );

  const handleRemoveFromBasket = useCallback(
    product => {
      const line = basket.productLineItems.find(line => line.product.id === product.id);
      removeProductFromBasket(line.product.id, line.productOption?.id, line.clinicLocation?.id);
    },
    [removeProductFromBasket, basket]
  );

  const allowMoreThanOne = basket?.purchaseAsStock || basket?.multiplePatientsEnabled;

  let addText = "Add";
  if (testProduct.options?.length || (!allowMoreThanOne && testProduct.addons?.length)) {
    addText = "Options";
  } else if (!allowMoreThanOne && hasBasketGotProduct(testProduct.id)) {
    addText = "Added";
  }

  if (testProduct.status === OUT_OF_STOCK) {
    addText = "Out of stock";
  } else if (testProduct.status === COMING_SOON) {
    addText = "Coming soon";
  }

  if (!testProduct.isStudentAllowed) {
    addText = "Not available";
  }

  const onAdd =
    allowMoreThanOne || (!allowMoreThanOne && !hasBasketGotProduct(testProduct.id))
      ? e => {
          e.onAdd = true;
          if (allowMoreThanOne || !hasBasketGotProduct(testProduct.id)) {
            handleAddToBasket(testProduct);
          }
        }
      : undefined;

  const cyrexBlockedForMultiPatient =
    basket?.multiplePatientsEnabled && testProduct.isCyrex && !hasBasketGotProduct(testProduct.id);

  const addDisabled =
    testProduct.status === OUT_OF_STOCK ||
    testProduct.status === COMING_SOON ||
    !testProduct.isStudentAllowed ||
    cyrexBlockedForMultiPatient ||
    (!allowMoreThanOne && hasBasketGotProduct(testProduct.id)) ||
    !permissions.includes(CMP_PERMISSION_PURCHASE_PRODUCTS);

  const onRemove =
    !allowMoreThanOne && hasBasketGotProduct(testProduct.id)
      ? e => {
          e.onRemove = true;
          handleRemoveFromBasket(testProduct);
        }
      : undefined;

  const showTradeOnCard =
    testProduct.tradeCurrentPrice != null && Number(testProduct.tradeCurrentPrice) !== 0;

  const priceSection = (
    <Group flexWrap="wrap" justifyContent="space-between" alignItems="center" mt="auto" gap={28}>
      <Stack>
        {showTradeOnCard ? (
          <Group>
            {testProduct.tradeFullPrice !== undefined &&
              testProduct.tradeFullPrice > testProduct.tradeCurrentPrice && (
                <GilroyFontCurrency
                  value={testProduct.tradeFullPrice}
                  symbol={basket?.currencySymbol}
                  data-testid="featuredTestProductCard:tradeFullPrice"
                  strikethrough
                  color="dark"
                />
              )}
            <GilroyFontCurrency
              value={testProduct.tradeCurrentPrice}
              symbol={basket?.currencySymbol}
              data-testid="featuredTestProductCard:tradeCurrentPrice"
              color={
                testProduct.tradeFullPrice !== undefined &&
                testProduct.tradeFullPrice > testProduct.tradeCurrentPrice
                  ? "red"
                  : "dark"
              }
            />
          </Group>
        ) : null}
        {showTradeOnCard ? (
          <GilroyFontCurrency
            prefix="RRP"
            color="midGrey"
            fontSize={[16, 16, 18]}
            decimalFontSize={[16, 16, 18]}
            symbol={basket?.currencySymbol}
            value={testProduct.exvatFullPrice}
            data-testid="featuredTestProductCard:retailFullPrice"
          />
        ) : null}
      </Stack>
      {onAdd && (
        <ButtonV2
          withChevron
          onClick={onAdd}
          disabled={addDisabled}
          color={addDisabled ? "midGrey" : "dark"}
          size={["sm", "sm", "md"]}
        >
          {addText}
        </ButtonV2>
      )}
      {onRemove && (
        <ButtonV2 onClick={onRemove} color="red" withChevron size={["sm", "sm", "md"]}>
          Remove
        </ButtonV2>
      )}
    </Group>
  );

  return (
    <FeaturedTestProductCard
      testProduct={testProduct}
      layout={useMobileLayout ? "grid" : "stacked"}
      priceSection={priceSection}
      onClick={e => {
        if (e.onAdd || e.onRemove) return;
        selectTestProductId(testProduct.id);
      }}
    />
  );
}

export default function FeaturedTestProductsCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);

  const { organisation } = useOrganisationContext();
  const { locale } = useLocalesContext();
  const { basket } = useOrganisationBasketContext();

  const { data, loading } = useQuery(PRACTITIONER_TEST_PRODUCT_CONNECTIONS_QUERY, {
    variables: {
      first: 20,
      after: "",
      orderBy: "sort_order",
      organisation: organisation?.id,
      featured: true,
      isoCode: locale?.isoCode,
      paymentIsoCode: basket?.paymentLocale?.isoCode
    },
    skip: !organisation?.id,
    fetchPolicy: "network-only"
  });

  const testProducts =
    data?.practitionerTestProductConnections?.edges?.map(edge => edge.node) ?? [];

  useEffect(() => {
    if (activeIndex >= testProducts.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, testProducts.length]);

  if (loading || testProducts.length === 0) {
    return null;
  }

  const activeProduct = testProducts[activeIndex] ?? testProducts[0];

  const showCarouselDots = testProducts.length > 1;

  return (
    <Box
      bg="#CBE1DA"
      borderRadius={20}
      width="100%"
      maxWidth={1024}
      mx="auto"
      mb={[20, 20, 40]}
      position="relative"
      key={activeProduct.id}
    >
      <FeaturedTestProductSlide testProduct={activeProduct} />
      {showCarouselDots && (
        <Center
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          height={96}
          gap={12}
          flexWrap="wrap"
          pointerEvents="none"
        >
          {testProducts.map((testProduct, index) => (
            <Box
              as="button"
              type="button"
              key={testProduct.id}
              width={20}
              height={20}
              borderRadius="50%"
              border="solid 1px"
              borderColor="dark"
              backgroundColor={activeIndex === index ? "dark" : "white"}
              aria-label={`Show featured test ${index + 1} of ${testProducts.length}`}
              aria-current={activeIndex === index ? "true" : undefined}
              onClick={() => setActiveIndex(index)}
              cursor="pointer"
              p={0}
              pointerEvents="auto"
            />
          ))}
        </Center>
      )}
    </Box>
  );
}
